import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import editCircleIcon from 'assets/images/icons/edit-circle.svg';
import searchIcon from 'assets/images/icons/search.svg';
import userGreyIcon from 'assets/images/icons/user-grey.svg';
import usersGreyIcon from 'assets/images/icons/groups.svg';
import DropdownWrapper from './DropdownWrapper';
import FormButton from 'components/form-components/FormButton';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';
import TextField from 'components/form-components/TextField';
import { CallAPI } from 'actions/General';
import TabHeaderNew from './TabHeaderNew';

const UserAndGroupSelection = ({
  toggleComponent,
  doneAction,
  existingUserIds,
  existingGroupIds,
  onDataLoad,
}) => {
  const tabList = [
    { value: 'users', displayValue: 'tabs_users', icon: userGreyIcon },
    { value: 'groups', displayValue: 'tabs_groups', icon: usersGreyIcon },
  ];

  const [selectedTab, setSelectedTab] = useState(tabList[0]);
  const [searchText, setSearchText] = useState('');
  const [allUserAndGroups, setAllUserAndGroups] = useState({
    users: [],
    groups: [],
  });
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  const hasFetched = useRef(false);
  const hasAppliedRef = useRef(false);

  useEffect(() => {
    setSearchText('');
  }, [selectedTab]);

  const fetchUsersAndGroups = async () => {
    const [userRes, groupRes] = await Promise.all([
      CallAPI('GET_ALL_SEARCH_ABLE_USERS'),
      CallAPI('GET_ALL_GROUPS'),
    ]);
    if (userRes.status && groupRes.status) {
      hasFetched.current = true;
      const allUsers = [];
      const allGroups = [];

      userRes.data.users.forEach((u) => {
        allUsers.push({ id: u._id, name: `${u.firstName} ${u.lastName}` });
      });
      groupRes.data.forEach((g) => {
        allGroups.push({ id: g._id, name: g.groupName });
      });

      const data = { users: allUsers, groups: allGroups };
      setAllUserAndGroups(data);
      onDataLoad?.(data);

      return data;
    }

    return { users: [], groups: [] };
  };

  const applyExistingData = (data) => {
    const checkedUserIds = [];
    const checkedGroupIds = [];

    (data?.users || allUserAndGroups.users).forEach((u) => {
      if (existingUserIds.includes(u.id)) checkedUserIds.push(u.id);
    });
    (data?.groups || allUserAndGroups.groups).forEach((g) => {
      if (existingGroupIds.includes(g.id)) checkedGroupIds.push(g.id);
    });

    setSelectedUserIds(checkedUserIds);
    setSelectedGroupIds(checkedGroupIds);
  };

  useEffect(() => {
    if (!onDataLoad || hasFetched.current) return;
    fetchUsersAndGroups();
  }, [onDataLoad]);

  useEffect(() => {
    if (
      hasFetched.current &&
      !hasAppliedRef.current &&
      (existingUserIds.length || existingGroupIds.length)
    ) {
      applyExistingData();
      hasAppliedRef.current = true;
    }
  }, [existingUserIds, existingGroupIds, allUserAndGroups, hasAppliedRef.current]);

  const handleSelectionChange = (type, id) => {
    if (type === 'user') {
      const tempSelection = new Set([...selectedUserIds]);
      if (tempSelection.has(id)) tempSelection.delete(id);
      else tempSelection.add(id);
      setSelectedUserIds([...tempSelection]);
    } else if (type === 'group') {
      const tempSelection = new Set([...selectedGroupIds]);
      if (tempSelection.has(id)) tempSelection.delete(id);
      else tempSelection.add(id);
      setSelectedGroupIds([...tempSelection]);
    }
  };

  const clearSelection = () => {
    setSelectedUserIds([]);
    setSelectedGroupIds([]);
  };

  const handleDoneClick = () => {
    const selectedAssignment = { users: [], groups: [] };

    allUserAndGroups.users.forEach((u) => {
      if (selectedUserIds.includes(u.id)) selectedAssignment.users.push(u.id);
    });
    allUserAndGroups.groups.forEach((g) => {
      if (selectedGroupIds.includes(g.id)) selectedAssignment.groups.push(g.id);
    });
    doneAction(selectedAssignment);
  };

  return (
    <div>
      <DropdownWrapper
        toggleClasses=''
        toggleComponent={toggleComponent ?? <img className='cursor-pointer' src={editCircleIcon} />}
        onToggle={async (open) => {
          if (open) {
            if (!onDataLoad && !hasFetched.current) {
              await fetchUsersAndGroups();
            }
          } else {
            clearSelection();
            setSearchText('');
            setSelectedTab(tabList[0]);
            hasAppliedRef.current = false;
          }
        }}
        dropdownContent={({ handleCloseDropdown }) => (
          <div className='px-3'>
            <div className='d-flex flex-column gap-3'>
              <TabHeaderNew
                tabList={tabList}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                classes={'d-flex theme-font-inter-medium theme-text-black-1'}
              />

              <TextField
                value={searchText}
                name='search'
                handleChange={(_, value) => setSearchText(value)}
                placeholder='field_search'
                icon={searchIcon}
                shrink
                classes='w-100'
              />

              {selectedTab.value === 'users' ? (
                <ListSelection
                  type='user'
                  allList={allUserAndGroups.users}
                  selectedIds={selectedUserIds}
                  handleChange={handleSelectionChange}
                  listTitle='text_users'
                  selectedListTitle='text_selected_users'
                  searchText={searchText}
                />
              ) : (
                <ListSelection
                  type='group'
                  allList={allUserAndGroups.groups}
                  selectedIds={selectedGroupIds}
                  handleChange={handleSelectionChange}
                  listTitle='text_groups'
                  selectedListTitle='text_selected_groups'
                  searchText={searchText}
                />
              )}
            </div>

            <div className='d-flex gap-3 justify-content-end pt-3 py-2 mt-2 border-top-grey-13-h-1'>
              <FormButton
                text='button_clear_selection'
                onClick={clearSelection}
                variant='white-1'
                minWidth
              />
              <FormButton
                text='button_done'
                variant='green-1'
                onClick={() => {
                  handleDoneClick();
                  setSearchText('');
                  setSelectedTab(tabList[0]);
                  handleCloseDropdown();
                }}
                disabled={
                  allUserAndGroups.users.length === 0 && allUserAndGroups.groups.length === 0
                }
                minWidth
              />
            </div>
          </div>
        )}
        zIndex={1055}
        fullWidth
      />
    </div>
  );
};

UserAndGroupSelection.propTypes = {
  toggleComponent: PropTypes.object,
  doneAction: PropTypes.func.isRequired,
  existingUserIds: PropTypes.array.isRequired,
  existingGroupIds: PropTypes.array.isRequired,
  onDataLoad: PropTypes.func,
};

const ListSelection = ({
  type,
  allList,
  selectedIds,
  handleChange,
  listTitle,
  selectedListTitle,
  searchText,
}) => {
  const { t } = useTranslation();

  const filteredSelected = allList.filter(
    (item) =>
      selectedIds.includes(item.id) && item.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  const filteredUnselected = allList.filter(
    (item) =>
      !selectedIds.includes(item.id) && item.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  return (
    <div style={{ maxHeight: 'min(22vh, 24rem)', overflow: 'auto' }}>
      <label className='d-flex text-uppercase theme-font-inter-medium theme-text-black-1'>
        {t(selectedListTitle)}
      </label>
      <div className='d-flex flex-column gap-2 my-2'>
        {filteredSelected.map((u, index) => (
          <SingleCheckBoxField
            key={index}
            name={`selected-${type}-${index}`}
            value={true}
            handleChange={() => handleChange(type, u.id)}
            text={<span className='theme-size-0_9'>{u.name}</span>}
            shrink
          />
        ))}
      </div>

      <label className='d-flex text-uppercase theme-font-inter-medium theme-text-black-1'>
        {t(listTitle)}
      </label>
      <div className='d-flex flex-column gap-2 my-2'>
        {filteredUnselected.map((u, index) => (
          <SingleCheckBoxField
            key={index}
            name={`unselected-${type}-${index}`}
            value={false}
            handleChange={() => handleChange(type, u.id)}
            text={<span className='theme-size-0_9'>{u.name}</span>}
            shrink
          />
        ))}
      </div>
    </div>
  );
};

ListSelection.propTypes = {
  type: PropTypes.string.isRequired,
  allList: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
  handleChange: PropTypes.func.isRequired,
  listTitle: PropTypes.string.isRequired,
  selectedListTitle: PropTypes.string.isRequired,
  searchText: PropTypes.string,
};

export default UserAndGroupSelection;
